"use client";

import {
  useImperativeHandle,
  useRef,
  type ReactNode,
  type FC,
  type Ref,
} from "react";
import {
  Badge,
  Button,
  Card,
  Flex,
  type FlexProps,
  Icon,
  IconButton,
  Slides,
  Spacing,
  Text,
  type SlidesRef,
} from "@huub-web/ui";
import { mergeClasses, toHumanCase } from "@huub-web/utils";

import type { Amenity } from "../../types";
import { containerStyle, cardInnerStyle } from "./MapControlCard.css";
import { formatDuration } from "./utils/formatters";

/**
 * Selected route data with amenity and route metrics
 */
type SelectedRoute = {
  amenity: Amenity & { center: [number, number] };
  distance: number;
  duration: number;
};

/**
 * Category data for filter buttons
 */
type Category = {
  id: string;
  label: string;
};

export type MapControlCardProps = {
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Available categories for filtering
   */
  categories: Category[];
  /**
   * Currently selected category IDs
   */
  selectedCategories: string[];
  /**
   * Handler for category toggle
   */
  onCategoryToggle: (categoryId: string) => void;
  /**
   * Function to get count for a category
   */
  getCategoryCount: (categoryId: string) => number;
  /**
   * Currently selected route (null when no route active)
   */
  selectedRoute: SelectedRoute | null;
  /**
   * Handler for back button click
   */
  onBack: () => void;
  /**
   * Whether the component is loading
   */
  justify?: FlexProps["justify"];
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<MapControlCardRef>;
};

export type MapControlCardRef = {
  /**
   * Navigate to filters view
   */
  showFilters: () => void;
  /**
   * Navigate to route view
   */
  showRoute: () => void;
};

/**
 * FiltersContent renders the category filter buttons
 */
export const FiltersContent = ({
  categories,
  selectedCategories,
  onCategoryToggle,
  getCategoryCount,
  justify = "flex-start",
  disabled = false,
}: Pick<
  MapControlCardProps,
  | "categories"
  | "selectedCategories"
  | "onCategoryToggle"
  | "getCategoryCount"
  | "justify"
  | "disabled"
>): ReactNode => (
  <Flex gap="xs" wrap align="center" justify={justify}>
    {categories.map((category) => (
      <Button
        key={category.id}
        variant="opaque"
        border="default"
        size="xs"
        active={selectedCategories.includes(category.id)}
        onClick={() => onCategoryToggle(category.id)}
        trailing={
          <Badge
            variant="tertiary"
            value={getCategoryCount(category.id)}
            inline
          />
        }
        disabled={disabled}
      >
        {category.label}
      </Button>
    ))}
  </Flex>
);

/**
 * RouteContent renders the active route details with back button
 */
const RouteContent = ({
  selectedRoute,
  onBack,
}: Pick<MapControlCardProps, "selectedRoute" | "onBack">): ReactNode => {
  if (!selectedRoute) {
    return null;
  }

  const { amenity, duration } = selectedRoute;

  return (
    <Flex direction="row" gap="s">
      <IconButton
        icon="chevronLeft"
        variant="background"
        size="xs"
        onClick={onBack}
        border="default"
        rounded
      />
      <Flex direction="column" gap="xxs" grow>
        <Spacing />
        <Text variant="bodyM" bold>
          {amenity.name}
        </Text>
        {amenity.type && (
          <Text variant="surfaceSubtitle" color="currentColor50">
            {toHumanCase(amenity.type)}
          </Text>
        )}
        <Spacing />
        <Flex direction="row" gap="xs">
          <Flex direction="row" gap="xxs" align="center">
            <Icon icon="walking" size="s" color="currentColor50" />
            <Text variant="bodyS" color="currentColor50">
              {formatDuration(duration)}
            </Text>
          </Flex>
          <Flex direction="row" gap="xxs" align="center">
            <Icon icon="pin" size="s" color="currentColor50" />
            <Text variant="bodyS" color="currentColor50">
              {selectedRoute.amenity.distance}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

/**
 * MapControlCard component displays either category filters or active route details.
 * Uses Slides component for smooth animated transitions between views.
 */
export const MapControlCard: FC<MapControlCardProps> = ({
  className,
  categories,
  selectedCategories,
  justify,
  selectedRoute,
  disabled = false,
  onCategoryToggle,
  getCategoryCount,
  onBack,
  ref,
}) => {
  const slidesRef = useRef<SlidesRef>(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    showFilters: () => slidesRef.current?.goTo("filters"),
    showRoute: () => slidesRef.current?.goTo("route"),
  }));

  const slides = {
    filters: (
      <FiltersContent
        categories={categories}
        selectedCategories={selectedCategories}
        onCategoryToggle={onCategoryToggle}
        getCategoryCount={getCategoryCount}
        justify={justify}
        disabled={disabled}
      />
    ),
    route: <RouteContent selectedRoute={selectedRoute} onBack={onBack} />,
  };

  // Don't render if no categories and no route
  if (categories.length === 0 && !selectedRoute) {
    return null;
  }

  return (
    <Card
      variant="primary"
      elevated
      className={mergeClasses(containerStyle, className)}
      innerClassName={cardInnerStyle}
      padding="none"
    >
      <Slides
        ref={slidesRef}
        slides={slides}
        initialSlide={selectedRoute ? "route" : "filters"}
      />
    </Card>
  );
};
